import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ExternalLink, Info, Lock, Pencil, Play, RefreshCw, Trash2 } from 'lucide-react';
import { lessonThumbnailSrc } from '../services/api';
import type { LessonSummary } from '../types/api';
import { gitHubProjectFileLink, gitHubWorldLabel, gitHubWorldLink, isGitHubWorldUrl } from '../utils/github';
import DifficultyBadge from './DifficultyBadge';
import ReadinessBadge from './ReadinessBadge';

interface LessonCardProps {
  lesson: LessonSummary;
  canManage: boolean;
  syncing: boolean;
  onPreview: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSync: () => void;
}

// One lesson in the Content Library (official and custom alike).
const LessonCard: React.FC<LessonCardProps> = ({ lesson, canManage, syncing, onPreview, onEdit, onDelete, onSync }) => {
  const isOfficial = lesson.lesson_type === 'official';
  const thumbnail = lessonThumbnailSrc(lesson);
  const worldLink = gitHubWorldLink(lesson.world_file);
  const editableFileLink = gitHubProjectFileLink(lesson.world_file, lesson.editable_file_path);

  return (
    <div data-testid="lesson-card" className="card p-4 sm:p-5 hover:border-gray-300 dark:hover:border-gray-700 group">
      <div className="flex flex-col md:flex-row gap-4 sm:gap-5">
        {thumbnail && (
          <div className="w-full md:w-44 h-36 md:h-28 flex-shrink-0">
            <img
              src={thumbnail}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover rounded-lg border border-gray-200 dark:border-gray-800"
            />
          </div>
        )}
        <div className="flex-1 min-w-0 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h3 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-white">{lesson.title}</h3>
              <DifficultyBadge difficulty={lesson.difficulty} />
              <ReadinessBadge readiness={lesson.readiness} />
              {!isOfficial && (
                <span className="badge badge-purple">
                  Custom
                </span>
              )}
            </div>
            {lesson.credit && (
              <div className="relative group/credit inline-flex items-center gap-1 mb-2 cursor-help select-none">
                <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                  By <span className="font-medium text-gray-700 dark:text-gray-300">{lesson.credit.name}</span>
                </span>
                {lesson.credit.short_description && <Info className="w-3 h-3 text-gray-400 flex-shrink-0" />}
                {lesson.credit.short_description && (
                  <div className="absolute bottom-full left-0 mb-2 w-72 max-w-[80vw] p-3 bg-gray-900 dark:bg-gray-700 rounded-xl shadow-2xl z-50 opacity-0 group-hover/credit:opacity-100 pointer-events-none transition-opacity duration-150">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ children }) => <p className="text-gray-200 text-xs mb-1 last:mb-0">{children}</p>,
                        strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                        a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline pointer-events-auto">{children}</a>,
                      }}
                    >{lesson.credit.short_description}</ReactMarkdown>
                  </div>
                )}
              </div>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{lesson.description}</p>

            {/* Links are rebuilt without the access token some world URLs carry. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <div className="min-w-0">
                <span className="eyebrow text-[11px]">World File</span>
                <p className="text-gray-600 dark:text-gray-400 truncate" title={gitHubWorldLabel(lesson.world_file)}>
                  {worldLink ? (
                    <a
                      href={worldLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1 max-w-full"
                    >
                      <span className="truncate">{gitHubWorldLabel(lesson.world_file)}</span>
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                  ) : (
                    gitHubWorldLabel(lesson.world_file)
                  )}
                </p>
              </div>
              <div className="min-w-0">
                <span className="eyebrow text-[11px]">Editable File</span>
                <p className="text-gray-600 dark:text-gray-400 truncate" title={lesson.editable_file_path}>
                  {editableFileLink ? (
                    <a
                      href={editableFileLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {lesson.editable_file_path}
                    </a>
                  ) : (
                    lesson.editable_file_path
                  )}
                </p>
              </div>
            </div>

            {lesson.tags && lesson.tags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {lesson.tags.map(tag => (
                  <span key={tag} className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-row flex-wrap items-center lg:items-stretch lg:flex-col gap-2 lg:ml-4 lg:w-40 shrink-0">
            <button
              onClick={onPreview}
              className="btn btn-secondary"
              title="Preview Lesson"
            >
              <Play />
              Preview
            </button>
            {isGitHubWorldUrl(lesson.world_file) && canManage && (
              <button
                onClick={onSync}
                disabled={syncing}
                className="btn btn-secondary"
              >
                <RefreshCw className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Syncing...' : 'Sync GitHub'}
              </button>
            )}
            {canManage && (
              <div className="flex gap-1 lg:justify-center">
                <button
                  onClick={onEdit}
                  className="btn btn-ghost btn-icon"
                  title="Edit Lesson"
                  aria-label="Edit Lesson"
                >
                  <Pencil />
                </button>
                <button
                  onClick={onDelete}
                  className="btn btn-ghost-danger btn-icon"
                  title="Delete Lesson"
                  aria-label="Delete Lesson"
                >
                  <Trash2 />
                </button>
              </div>
            )}
            {isGitHubWorldUrl(lesson.world_file) ? (
              !canManage && (
                <div className="flex items-center justify-center gap-1.5 text-gray-500 dark:text-gray-400 text-xs px-2 py-1">
                  <Lock className="w-3.5 h-3.5" />
                  <span>Official Content</span>
                </div>
              )
            ) : (
              <div className="flex items-center justify-center gap-1.5 text-gray-500 dark:text-gray-400 text-xs px-2 py-1">
                <Info className="w-3.5 h-3.5" />
                <span>No repository linked</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonCard;
